import Vue from "vue";
import service from "@/store/services/trainings-service";

// One request per help topic for the whole session: several "?" buttons of the
// same screen, or a return to the screen, reuse the first answer.
const pendingHelp = {};

const state = () => ({
  help: [],
  helpByContext: {},
  myPath: { files: [], total: 0, completed: 0, must_watch_videos: false },
  myPathLoaded: false,
  paths: [],
  followUp: [],
  settings: { force_video_completion: false, exempt_users: [], users: [] },
});

// The same file can be loaded several times: help topics, help by screen and
// the training path.
function forEachLoadedFile(state, fileId, callback) {
  const files = [
    ...Object.values(state.helpByContext).flat(),
    ...state.help.flatMap((topic) => topic.files),
    ...state.myPath.files,
  ];
  files.filter((file) => file.id === fileId).forEach(callback);
}

const mutations = {
  SET_HELP: (state, help) => {
    state.help = help;
    help.forEach(({ context, files }) => {
      Vue.set(state.helpByContext, context, files);
    });
  },
  SET_HELP_CONTEXT: (state, { context, files }) => {
    Vue.set(state.helpByContext, context, files);
    const topic = state.help.find((item) => item.context === context);
    if (topic) {
      topic.files = files;
    }
  },
  SET_MY_PATH: (state, myPath) => {
    state.myPath = myPath;
    state.myPathLoaded = true;
  },
  SET_COMPLETED: (state, { fileId, completedAt }) => {
    forEachLoadedFile(state, fileId, (file) => {
      file.completed_at = completedAt;
    });
    state.myPath.completed = state.myPath.files.filter(
      (file) => !!file.completed_at
    ).length;
  },
  SET_PROGRESS: (state, { fileId, watchedSeconds, durationSeconds }) => {
    forEachLoadedFile(state, fileId, (file) => {
      file.watched_seconds = watchedSeconds;
      file.duration_seconds = durationSeconds;
    });
  },
  SET_PATHS: (state, paths) => {
    state.paths = paths;
  },
  SET_PATH: (state, path) => {
    const index = state.paths.findIndex((item) => item.id === path.id);
    if (index >= 0) {
      state.paths.splice(index, 1, path);
    }
  },
  SET_FOLLOW_UP: (state, followUp) => {
    state.followUp = followUp;
  },
  SET_REMINDED: (state, { userId, remindedAt }) => {
    const user = state.followUp.find((item) => item.id === userId);
    if (user) {
      user.reminded_at = remindedAt;
    }
  },
  SET_SETTINGS: (state, settings) => {
    state.settings = settings;
  },
};

const actions = {
  help({ commit }) {
    return service.help().then((help) => {
      commit("SET_HELP", help);
    });
  },

  helpForContext({ commit, state }, { context, force = false }) {
    if (!force && state.helpByContext[context]) {
      return Promise.resolve(state.helpByContext[context]);
    }
    if (!pendingHelp[context]) {
      pendingHelp[context] = service
        .help(context)
        .then(([topic]) => {
          commit("SET_HELP_CONTEXT", { context, files: topic.files });
          return topic.files;
        })
        .finally(() => {
          delete pendingHelp[context];
        });
    }
    return pendingHelp[context];
  },

  updateHelp({ commit }, { context, fileIds }) {
    return service.updateHelp(context, fileIds).then(([topic]) => {
      commit("SET_HELP_CONTEXT", { context, files: topic.files });
    });
  },

  myPath({ commit }) {
    return service.myPath().then((myPath) => {
      commit("SET_MY_PATH", myPath);
    });
  },

  setCompleted({ commit }, { fileId, completed }) {
    return service.setCompleted(fileId, completed).then((result) => {
      commit("SET_COMPLETED", {
        fileId,
        completedAt: result.completed_at,
      });
    });
  },

  saveProgress({ commit }, { fileId, position, duration }) {
    return service.saveProgress(fileId, position, duration).then((result) => {
      commit("SET_PROGRESS", {
        fileId,
        watchedSeconds: result.watched_seconds,
        durationSeconds: result.duration_seconds,
      });
    });
  },

  paths({ commit }) {
    return service.paths().then((paths) => {
      commit("SET_PATHS", paths);
    });
  },

  updatePath({ commit }, { roleId, fileIds }) {
    return service.updatePath(roleId, fileIds).then((path) => {
      commit("SET_PATH", path);
    });
  },

  followUp({ commit }) {
    return service.followUp().then((followUp) => {
      commit("SET_FOLLOW_UP", followUp);
    });
  },

  remind({ commit }, userId) {
    return service.remind(userId).then((result) => {
      commit("SET_REMINDED", { userId, remindedAt: result.reminded_at });
    });
  },

  settings({ commit }) {
    return service.settings().then((settings) => {
      commit("SET_SETTINGS", settings);
    });
  },

  async updateSettings(
    { commit, dispatch, state },
    { forceVideoCompletion, exemptUserIds }
  ) {
    const settings = await service.updateSettings(
      forceVideoCompletion,
      exemptUserIds
    );
    commit("SET_SETTINGS", settings);
    // The manager's own videos may have to be watched to the end now.
    await Promise.all([
      dispatch("myPath"),
      state.help.length ? dispatch("help") : null,
    ]);
  },
};

const getters = {
  help: (state) => state.help,
  helpForContext: (state) => (context) => state.helpByContext[context] || [],
  myPath: (state) => state.myPath,
  myPathLoaded: (state) => state.myPathLoaded,
  paths: (state) => state.paths,
  followUp: (state) => state.followUp,
  settings: (state) => state.settings,
};

const trainings = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default trainings;
